import { isEmpty } from '../utilities/general'
import camelcaseKeys from 'camelcase-keys'
import { RealtimePostgresChangesPayload } from '@supabase/realtime-js'
import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import { callTool } from '../lib/tools'
import useOrganizations from './useOrganizations'
import { readNodesAgain } from './useNode'
import useNodeAttach from './useNodeAttach'
import { keptPrivateNames, numberedNames } from '../utilities/nodeAttach'
import { networkCreateArgs, networkIsolationArgs, networkMembersMayAddNodesArgs, type NetworkCreateOptions } from '../utilities/networkSettings'
import { networkRemoveRefusal } from '../utilities/routerWrites'

export interface NetworkState {
  networks: NetworkRecord[]
  /**
   * The org of the last read of `networks`, or `undefined` before the first read. A reader that
   * needs the COMPLETE list of the active org's networks tests this against the active org
   * (app#388: `hooks/useOutsideNodes`). While the read has not come back, or after an org switch
   * before the new read comes back, an empty or old list does not say that a network is another
   * org's.
   */
  loadedOrgId: string | null | undefined
  set: (state: Partial<NetworkState>) => void
  get: () => Promise<void>
  /**
   * The org of network `id`: a direct read of the one row. `false` when the reader can read no
   * such network, null when the read failed (app#478). RLS bounds the read to the reader's orgs.
   */
  orgOf: (id: string) => Promise<string | false | null>
  update: (network: Partial<NetworkRecord>) => void
  rename: (id: string, name: string) => Promise<void>
  setChannel: (id: string, channelId: string | null) => Promise<void>
  setIsolated: (id: string, isolated: boolean) => Promise<void>
  setMembersMayAddNodes: (id: string, membersMayAddNodes: boolean) => Promise<void>
  add: (network: Partial<NetworkRecord> & Pick<NetworkRecord, 'name'>, options?: NetworkCreateOptions) => Promise<void>
  delete: (id: string) => Promise<string | null>
  subscribe: () => () => void
  reset: () => void
}

const defaultNetwork: NetworkRecord = {
  createdAt: '',
  name: '',
  ownerId: null,
  orgId: null,
  channelId: null,
}

const state: Pick<NetworkState, 'networks' | 'loadedOrgId'> = {
  networks: [],
  loadedOrgId: undefined,
}

const useNetwork = create<NetworkState>()((set, get) => ({
  ...state,
  set,
  get: async () => {
    // Scope to the active org (1.3). RLS still permits every member org, so this
    // is UX scoping, not a security boundary.
    const activeOrgId = useOrganizations.getState().activeOrgId
    let query = supabase
      .from('networks')
      .select('id, created_at, name, owner_id, org_id, channel_id, isolated')
      .is('deleted_at', null)
    if (activeOrgId) query = query.eq('org_id', activeOrgId)

    // ⚠⚠ "MEMBERS MAY ADD NODES" IS ITS OWN READ, AND ITS FAILURE IS IGNORED (app#376). The column
    // comes with router migration 0230 (router#1761). Named in the select above, a database without
    // it would fail the WHOLE list with 42703, and every page that lists networks would be empty.
    // Read apart, a missing column costs only the setting, which then reads as on: the column
    // default, and the rule before the setting existed.
    let settingsQuery = supabase.from('networks').select('id, members_may_add_nodes').is('deleted_at', null)
    if (activeOrgId) settingsQuery = settingsQuery.eq('org_id', activeOrgId)

    const [{ data, error }, settings] = await Promise.all([query, settingsQuery])
    if (error) console.warn(error)
    else {
      const allowed = new Map<string, boolean>()
      if (!settings.error) {
        for (const row of (settings.data ?? []) as { id: string; members_may_add_nodes?: boolean | null }[]) {
          if (typeof row.members_may_add_nodes === 'boolean') allowed.set(row.id, row.members_may_add_nodes)
        }
      }
      const networks: NetworkRecord[] = camelcaseKeys(data).map((n: NetworkRecord) =>
        n.id && allowed.has(n.id) ? { ...n, membersMayAddNodes: allowed.get(n.id) } : n
      )
      set({ networks, loadedOrgId: activeOrgId })
      console.log('get networks', networks)
    }
  },
  orgOf: async id => {
    const { data, error } = await supabase.from('networks').select('org_id').eq('id', id).is('deleted_at', null).limit(1)
    // ⚠ An id that is not a uuid (a path typed or cut by hand) is no object: the database refuses
    // the comparison (22P02). That is a clear "no", not a failed read.
    if (error?.code === '22P02') return false
    if (error) {
      console.warn(error)
      return null
    }
    return (data ?? [])[0]?.org_id || false
  },
  update: network => {
    const { id, ...data } = network
    const networks = [...get().networks]

    const index = networks.findIndex(t => t.id === id)
    if (index === -1) {
      // A tombstone for a network we are not holding is nothing to ADD. get() excludes
      // deleted rows, so pushing one here would put a deleted network on screen — the
      // exact inverse of the bug the splice below fixes. delete() soft-deletes, so the
      // deletion arrives as an in-org UPDATE and lands right here.
      //
      // An EMPTY payload is the same answer for the same reason. This is the only one of
      // the three stores whose handler routes a realtime DELETE through update() — it
      // passes just an id, and unlike the soft-delete path it is deliberately exempt from
      // the org guard above — so "remove a network we are not holding" arrives here too,
      // and without this it pushes a nameless defaultNetwork into the list. Nothing
      // hard-deletes a network today (the router's GORM model is soft-delete, so is
      // delete()), which is why this is a guard and not a bug report.
      if (data.deletedAt || isEmpty(data)) return
      networks.push({ ...defaultNetwork, ...network })
    } else if (isEmpty(data) || data.deletedAt) {
      networks.splice(index, 1)
    } else {
      networks[index] = { ...networks[index], ...network }
    }

    set({ networks })
  },
  // ⚠⚠ RENAME AND CHANNEL GO THROUGH THE ROUTER'S `network_update` (app#373, docs/PERMISSIONS.md
  // § 5 Network: "Rename, channel, isolation — network owner or Admin"). They were PostgREST writes
  // behind the networks UPDATE policy (org Owner/Admin only), so a Member could not rename the
  // network they created, and the policy refused in its USING clause — zero rows, no error — which
  // is why rename had to ask for the row back to notice. The router answers `forbidden` or
  // `already_exists` instead.
  //
  // Both THROW rather than alerting, so the page can put a readable sentence under the field
  // (`renameErrorMessage` for the rename).
  // ⚠ `network_id`, never `network_name`: a name can be the same in two of the reader's orgs.
  // ⚠ The router lower-cases the name before it stores it; realtime brings the stored row back.
  rename: async (id, name) => {
    await callTool('network_update', { network_id: id, name })
  },
  // ⚠ An empty `channel_id` clears it (the default channel). Absent would mean "do not change".
  setChannel: async (id, channelId) => {
    await callTool('network_update', { network_id: id, channel_id: channelId ?? '' })
  },
  // ⚠ Client isolation (docs/PERMISSIONS.md § 6, router#1769): the network's owner or an org
  // Owner/Admin sets it through `network_update`. Realtime brings the row back.
  setIsolated: async (id, isolated) => {
    await callTool('network_update', networkIsolationArgs(id, isolated))
  },
  // ⚠ "Members may add nodes" (docs/PERMISSIONS.md § 5 Network, router#1761): the network's owner or
  // an org Owner/Admin changes it through `network_update`. The store takes the router's answer at
  // once, because the setting is read in its own select (see `get`), and realtime also brings it.
  setMembersMayAddNodes: async (id, membersMayAddNodes) => {
    const res = await callTool<{ members_may_add_nodes?: boolean }>(
      'network_update',
      networkMembersMayAddNodesArgs(id, membersMayAddNodes)
    )
    const value = typeof res?.members_may_add_nodes === 'boolean' ? res.members_may_add_nodes : membersMayAddNodes
    get().update({ id, membersMayAddNodes: value })
  },
  // ⚠⚠ DELETE GOES THROUGH THE ROUTER'S `network_delete` (app#373, docs/PERMISSIONS.md § 5 Network:
  // "Delete — network owner or Admin"). It sends `network_id`, never `network_name`: a name can be
  // the same in two of the reader's orgs (router#1829 added `network_id`).
  // ⚠ It never deletes a node. Since router 0.4.211 its nodes leave the network (router#2139), so
  // the nodes are read again: nothing else tells the store about a row of `node_networks`.
  //
  // Returns null when the network was removed, else the refusal as ONE sentence, which the page
  // shows under the button (app#376; it was an `alert()`). The page leaves only on null.
  delete: async id => {
    let payload: unknown
    try {
      payload = await callTool('network_delete', { network_id: id })
    } catch (e) {
      console.error('network_delete failed:', e)
      return networkRemoveRefusal(e)
    }
    await readNodesAgain()
    // ⚠ The page goes away with the network, so the one dialog says that a name changed (PRD MN-5),
    // and which nodes the router set to Private because the caller does not control them
    // (router#2200).
    const names = numberedNames(payload)
    const keptPrivate = keptPrivateNames(payload)
    if (names.length || keptPrivate.length) useNodeAttach.getState().open({ mode: 'numbered', names, keptPrivate })
    return null
  },
  add: async (network, options) => {
    // Route creation through the router tools client so org_id is stamped
    // server-side from the active org (the client is not trusted to set
    // ownership), and the router creates the default token. See PLAN_1.md →
    // "Where the role gate lives".
    const activeOrgId = useOrganizations.getState().activeOrgId
    // ⚠⚠ THROWS RATHER THAN ALERTING, AND THAT IS THE FIX (app#296). The refusal this call meets
    // most often is a spent allowance, which the router answers as `resource_exhausted` with a
    // typed payload naming the way out — and a native `alert()` could render neither the payload
    // nor anything that looks like this app, while freezing the page until it was dismissed. The
    // caller owns the wording now: every one of the three renders `CreateRefusal` inside its own
    // surface. Same shape as `rename` above, for the same reason.
    //
    // ⚠ Any member creates a network and becomes its owner (docs/PERMISSIONS.md § 5 Network, Q-3).
    // `options` are the two settings a network can start with (app#376); each is sent only when it
    // differs from the router's default, see `networkCreateArgs`.
    await callTool('network_create', networkCreateArgs(network.name, activeOrgId, options))
    await get().get()
  },
  subscribe: () => {
    const { update } = get()

    const handleChange = (payload: RealtimePostgresChangesPayload<NetworkRecord>) => {
      console.log('Network change received!', payload)
      const newNet = payload.new ? (payload.new as NetworkRecord) : null
      const oldNet = payload.old ? (payload.old as NetworkRecord) : null
      // Stay scoped to the active org: get() filters by org_id, so ignore realtime
      // events for a network in another org you belong to — otherwise it leaks into
      // the view and mixes orgs. (DELETE is safe to apply unconditionally.)
      const activeOrgId = useOrganizations.getState().activeOrgId
      const eventOrgId = (payload.new as { org_id?: string } | null)?.org_id ?? null
      if (activeOrgId && payload.eventType !== 'DELETE' && eventOrgId !== activeOrgId) return
      update({ ...camelcaseKeys(payload.new), id: newNet?.id || oldNet?.id })
      // ⚠ A delete takes every node out of this network, and no event on `nodes` says so for a node
      // in another network too. Soft-deleted: an UPDATE, not a DELETE.
      if ((payload.new as { deleted_at?: string | null } | null)?.deleted_at) void readNodesAgain()
    }

    const subscription = supabase
      .channel('networks-all')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'networks' }, handleChange)
      .subscribe()

    return () => {
      console.log('Unsubscribing from network changes')
      supabase.removeChannel(subscription)
    }
  },
  reset: () => set({ ...state }),
}))

export default useNetwork
